import NAV_PATHS from '@lib/const/nav-paths.const'

type Locale = 'tr' | 'en'

type LocalePathPair = {
  tr: string
  en: string
}

/**
 * Rewrite'larda olup NAV_PATHS'te eksik kalan path prefix'leri.
 */
const EXTRA_LOCALE_PATH_PAIRS: LocalePathPair[] = [
  {
    tr: '/sergiler-ve-etkinlikler/film-ve-video',
    en: '/exhibitions-and-events/movie-and-video',
  },
]

const normalizePathname = (path: string): string => {
  let pathname = path.split('?')[0] || '/'

  if (pathname.startsWith('/en/')) {
    pathname = pathname.slice(3)
  } else if (pathname === '/en') {
    pathname = '/'
  } else if (pathname.startsWith('/tr/')) {
    pathname = pathname.slice(3)
  } else if (pathname === '/tr') {
    pathname = '/'
  }

  if (pathname.length > 1 && pathname.endsWith('/')) {
    pathname = pathname.slice(0, -1)
  }

  return pathname || '/'
}

const isInternalPath = (path: string) =>
  path.startsWith('/') && !path.startsWith('//')

/**
 * NAV_PATHS + ekstra rewrite çiftlerinden TR↔EN path listesi.
 * En uzun prefix önce eşleşsin diye uzunluğa göre sıralanır.
 */
export const getLocalePathPairs = (): LocalePathPair[] => {
  const pairMap = new Map<string, LocalePathPair>()

  Object.values(NAV_PATHS).forEach((paths) => {
    const tr = normalizePathname(paths.tr)
    const en = normalizePathname(paths.en)

    if (!isInternalPath(paths.tr) || !isInternalPath(paths.en)) {
      return
    }

    if (tr === en) {
      return
    }

    pairMap.set(`${tr}::${en}`, { tr, en })
  })

  EXTRA_LOCALE_PATH_PAIRS.forEach((pair) => {
    const tr = normalizePathname(pair.tr)
    const en = normalizePathname(pair.en)
    pairMap.set(`${tr}::${en}`, { tr, en })
  })

  return [...pairMap.values()].sort((a, b) => b.tr.length - a.tr.length)
}

/**
 * Mevcut asPath'i hedef locale'in path diline çevirir.
 * Örn: /koleksiyonlar-ve-arastirmalar/arsivler/337 + en
 *   -> /collections-and-research/archives/337
 */
export const getLocalizedPath = (
  asPath: string,
  targetLocale: Locale
): string => {
  const hashIndex = asPath.indexOf('#')
  const hash = hashIndex >= 0 ? asPath.slice(hashIndex) : ''
  const withoutHash = hashIndex >= 0 ? asPath.slice(0, hashIndex) : asPath
  const [pathnameRaw, search = ''] = withoutHash.split('?')
  const query = search ? `?${search}` : ''
  const pathname = normalizePathname(pathnameRaw || '/')

  const fromLocale: Locale = targetLocale === 'en' ? 'tr' : 'en'
  const pairs = getLocalePathPairs().sort(
    (a, b) => b[fromLocale].length - a[fromLocale].length
  )

  for (const pair of pairs) {
    const fromPath = pair[fromLocale]
    const toPath = pair[targetLocale]

    if (pathname === fromPath) {
      return `${toPath}${query}${hash}`
    }

    if (pathname.startsWith(`${fromPath}/`)) {
      return `${toPath}${pathname.slice(fromPath.length)}${query}${hash}`
    }
  }

  return `${pathname}${query}${hash}`
}
